import { api } from './api';
import { processSubscriptionPayment } from './paymentService';

/**
 * Récupère les plans d'abonnement disponibles
 * @returns {Promise<Array>} - Liste des plans d'abonnement
 */
export const getSubscriptionPlans = async () => {
  try {
    const response = await api.get('/contracts/subscription-plans');
    
    // Vérifier la structure de la réponse
    if (!response || !response.data) {
      console.error('Réponse API invalide:', response);
      return [];
    }
    
    // Extraire les données en fonction de la structure de la réponse
    let plansData = [];
    
    // Essayer différentes structures de réponse possibles
    if (Array.isArray(response.data)) {
      plansData = response.data; // Si la réponse est directement un tableau
    } else if (response.data.data && Array.isArray(response.data.data)) {
      plansData = response.data.data; // Si la réponse est dans une propriété data
    } else if (response.data.plans && Array.isArray(response.data.plans)) {
      plansData = response.data.plans; // Si la réponse est dans une propriété plans
    } else if (response.data.subscriptionPlans && Array.isArray(response.data.subscriptionPlans)) {
      plansData = response.data.subscriptionPlans; // Si la réponse est dans une propriété subscriptionPlans
    }
    
    
    // Transformer les données pour correspondre au format attendu par le frontend
    const plans = plansData.map(plan => {
      // Créer un objet avec des valeurs par défaut
      const processedPlan = {
        id: plan.plan_id || plan.id || '',
        name: plan.name || 'Sans nom',
        description: plan.description || 'Aucune description disponible',
        price: Number(plan.price) || 0,
        currency: plan.currency || 'TND',
        features: Array.isArray(plan.features) ? plan.features : [],
        billing_cycle: plan.billing_cycle || 'monthly',
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        is_popular: plan.is_popular || false
      };
      
      // Ajouter toutes les autres propriétés non standard
      Object.keys(plan).forEach(key => {
        if (!(key in processedPlan)) {
          processedPlan[key] = plan[key];
        }
      });
      
      return processedPlan;
    });
    
    return plans;
  } catch (error) {
    console.error('Erreur lors de la récupération des plans d\'abonnement:', {
      error: error.message,
      response: error.response?.data
    });
    throw new Error(error.message || 'Erreur lors de la récupération des plans d\'abonnement');
  }
};

/**
 * Récupère les détails d'un plan spécifique
 * @param {string} planId - ID du plan
 * @returns {Promise<Object>} - Détails du plan
 */
export const getPlanDetails = async (planId) => {
  try {
    const response = await api.get(`/subscriptions/plans/${planId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des détails du plan:', error);
    throw new Error(error.message || 'Erreur lors de la récupération des détails du plan');
  }
};

/**
 * Récupère l'abonnement actuel de l'utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - L'abonnement de l'utilisateur
 */
/**
 * Vérifie le statut de la période d'essai d'un déménageur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Statut de la période d'essai
 */
/**
 * Vérifie le statut de la période d'essai d'un déménageur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Statut de la période d'essai
 */
export const checkTrialStatus = async (userId) => {
  try {
    
    // Vérifier si l'utilisateur est connecté
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Aucun token d\'authentification trouvé');
      return {
        isTrialActive: false,
        hasActiveContract: false,
        code: 'NOT_AUTHENTICATED',
        message: 'Utilisateur non authentifié',
        requiresAction: true
      };
    }

    // Essayer d'abord avec le nouvel endpoint
    try {
      const response = await api.getTrialStatus(userId);
      
      if (!response) {
        throw new Error('Réponse vide du serveur');
      }
      
      // S'assurer que la réponse contient les champs nécessaires
      const trialData = {
        isTrialActive: response.isTrialActive !== false, // Par défaut true si non défini
        hasActiveContract: response.hasActiveContract === true,
        code: response.code || (response.isTrialActive !== false ? 'TRIAL_ACTIVE' : 'TRIAL_EXPIRED'),
        trialEndDate: response.trialEndDate,
        message: response.message || 'Statut de période d\'essai récupéré',
        requiresAction: response.requiresAction === true,
        contractType: response.contractType || null
      };
      
      return trialData;
      
    } catch (error) {
      console.error('Erreur avec le nouvel endpoint, tentative avec l\'ancien endpoint:', error);
      
      // Essayer avec l'ancien endpoint en cas d'échec
      try {
        const oldResponse = await api.get(`/users/${userId}/trial-status`);
        
        return {
          isTrialActive: oldResponse.isTrialActive !== false,
          hasActiveContract: oldResponse.hasActiveContract === true,
          code: oldResponse.code || 'TRIAL_ACTIVE',
          trialEndDate: oldResponse.trialEndDate,
          message: oldResponse.message || 'Statut de période d\'essai récupéré (ancien endpoint)',
          requiresAction: oldResponse.requiresAction === true,
          contractType: oldResponse.contractType || null
        };
      } catch (oldError) {
        console.error('Erreur avec l\'ancien endpoint:', oldError);
        throw oldError; // Propager l'erreur pour une gestion ultérieure
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la vérification de la période d\'essai:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    // Si la période d'essai est terminée (403)
    if (error.response?.status === 403) {
      const trialData = {
        isTrialActive: false,
        hasActiveContract: false,
        code: 'TRIAL_EXPIRED',
        trialEndDate: error.response.data?.trialEndDate,
        message: error.response.data?.message || 'Votre période d\'essai est terminée. Veuillez choisir un type de contrat.',
        requiresAction: true,
        contractType: null
      };
      return trialData;
    }
    
    // En cas d'erreur 404, essayer avec l'ancien endpoint
    if (error.response?.status === 404) {
      try {
        const oldResponse = await api.get(`/users/${userId}/trial-status`);
        if (oldResponse) {
          return {
            isTrialActive: oldResponse.isTrialActive !== false,
            hasActiveContract: oldResponse.hasActiveContract === true,
            code: oldResponse.code || 'TRIAL_ACTIVE',
            trialEndDate: oldResponse.trialEndDate,
            message: oldResponse.message || 'Statut de période d\'essai récupéré (ancien endpoint)',
            requiresAction: oldResponse.requiresAction === true,
            contractType: oldResponse.contractType || null
          };
        }
      } catch (oldError) {
        console.error('Échec de la récupération avec l\'ancien endpoint:', oldError);
        // En cas d'échec avec l'ancien endpoint, retourner un état par défaut
        return {
          isTrialActive: true,
          hasActiveContract: false,
          code: 'TRIAL_ACTIVE',
          message: 'Période d\'essai active (par défaut)',
          requiresAction: false,
          contractType: null
        };
      }
    }
    
    // En cas d'autre erreur, retourner un état par défaut sécurisé
    console.warn('Erreur inattendue lors de la vérification de la période d\'essai, retour d\'un état par défaut');
    return { 
      isTrialActive: true,
      hasActiveContract: false,
      code: 'TRIAL_ACTIVE',
      message: 'Période d\'essai active (par défaut)',
      requiresAction: false,
      contractType: null
    };
  }
};

/**
 * Récupère l'abonnement actuel de l'utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Abonnement de l'utilisateur
 */
export const getSubscription = async (userId) => {
  try {
    const response = await api.get(`/subscriptions/${userId}/subscription`);
    
    
    // Vérifier si la réponse contient des données
    if (response.data) {
      // Si la réponse est un tableau, prendre le premier élément actif
      if (Array.isArray(response.data)) {
        const activeSubscription = response.data.find(sub => sub.status === 'active');
        return activeSubscription || null;
      }
      // Si c'est un objet unique, le retourner directement
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'abonnement:', {
      error: error.message,
      response: error.response?.data
    });
    
    // Si c'est une erreur 404, cela signifie qu'aucun abonnement n'a été trouvé
    if (error.response?.status === 404) {
      return null;
    }
    
    throw error;
  }
};

/**
 * Souscrit à un plan d'abonnement
 * @param {Object} data - Données de souscription
 * @param {string} data.userId - ID de l'utilisateur
 * @param {string} data.planId - ID du plan de souscription
 * @param {string} data.paymentMethodId - ID de la méthode de paiement Stripe
 * @param {string} data.amount - Montant de l'abonnement
 * @param {string} data.currency - Devise (par défaut: 'TND')
 * @returns {Promise<Object>} - La réponse du serveur
 */
export const subscribeToPlan = async ({ 
  userId, 
  planId, 
  paymentMethodId,
  amount,
  currency = 'EUR',
  renewalType = 'automatic' // 'automatic' or 'manual'
}) => {
  // Déclarer paymentResult au niveau de la fonction pour qu'il soit accessible partout
  let paymentResult = null;
  
  try {

    
    // 0. Vérifier d'abord s'il y a un abonnement actif
    const currentSubscription = await getSubscription(userId);
    if (currentSubscription) {
      
      try {
        // Annuler l'abonnement existant avant d'en créer un nouveau
        await cancelSubscription(userId);
        
        // Attendre un court instant pour s'assurer que la base de données a mis à jour l'état
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Vérifier que l'abonnement a bien été annulé
        const updatedSubscription = await getSubscription(userId);
        if (updatedSubscription && updatedSubscription.status === 'active') {
          throw new Error('Impossible d\'annuler l\'abonnement existant');
        }
      } catch (cancelError) {
        console.error('Erreur lors de l\'annulation de l\'abonnement existant:', cancelError);
        throw new Error('Impossible de mettre à jour votre abonnement. Veuillez annuler manuellement votre abonnement actif avant d\'en souscrire un nouveau.');
      }
    }
    
    // 1. Traiter le paiement uniquement après avoir vérifié/cancelé l'abonnement existant
    if (paymentMethodId) {
      try {
        paymentResult = await processSubscriptionPayment({
          amount,
          currency,
          paymentMethodId,
          subscriptionId: planId
        });
      } catch (paymentError) {
        console.error('Erreur lors du traitement du paiement:', paymentError);
        // Si le paiement échoue, on relance l'erreur avec un message plus clair
        throw new Error(`Échec du paiement: ${paymentError.message || 'Veuillez vérifier vos informations de paiement'}`);
      }
    }

    let response;
    
    try {
      // Utiliser la route correcte et le bon format de données
      response = await api.post('/subscriptions/subscribe', {
        plan_id: planId,
        payment_method: paymentMethodId ? 'stripe' : 'manual',
        payment_reference: paymentMethodId || null,
        payment_id: paymentResult?.payment?.id || null,
        renewal_type: renewalType,
        userId: userId // Ajouter l'ID de l'utilisateur dans le corps de la requête
      });
      
      
      // Si la réponse est un succès (même si data est vide), on considère que c'est bon
      if (response && response.status >= 200 && response.status < 300) {
        // Si on arrive ici, la création a réussi
        return {
          success: true,
          message: 'Abonnement créé avec succès',
          subscription: response.data || { plan_id: planId, status: 'active' }
        };
      }
      
      // Si on arrive ici, il y a un problème avec la réponse
      throw new Error('Réponse inattendue du serveur');
    } catch (error) {
      // Si l'erreur indique qu'un abonnement existe déjà, on vérifie si le paiement a réussi
      if (error.response?.data?.message?.includes('existe déjà') || 
          error.response?.data?.code === 'SUBSCRIPTION_ALREADY_ACTIVE') {
        console.log('Un abonnement existe déjà, vérification du statut...');
        
        // Si le paiement a réussi, on considère que c'est un succès
        if (paymentResult?.success) {
          return {
            success: true,
            message: 'Votre abonnement est actif',
            subscription: { plan_id: planId, status: 'active' }
          };
        }
      }
      
      // Pour toutes les autres erreurs, on les propage
      throw error;
    }
    
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la souscription:', {
      error: error.response?.data || error.message,
      status: error.response?.status
    });
    
    // Si le paiement a réussi mais qu'il y a une erreur de souscription
    if (paymentResult?.success) {
      // On retourne quand même un succès car le paiement a été traité
      return {
        success: true,
        message: 'Votre paiement a été traité avec succès. Veuillez rafraîchir la page pour voir votre abonnement.',
        subscription: { plan_id: planId, status: 'active' },
        paymentProcessed: true
      };
    }
    
    // Gestion des erreurs spécifiques
    if (error.response?.data?.code === 'SUBSCRIPTION_ALREADY_ACTIVE' || 
        error.response?.data?.message?.includes('existe déjà')) {
      return {
        success: true,
        message: 'Votre abonnement est déjà actif',
        subscription: { plan_id: planId, status: 'active' }
      };
    }
    
    // Si c'est une erreur de paiement
    if (error.response?.data?.code?.includes('PAYMENT_')) {
      throw new Error(error.response.data.message || 'Erreur lors du traitement du paiement');
    }
    
    // Pour toutes les autres erreurs, on vérifie si le paiement a réussi
    if (paymentResult?.success) {
      return {
        success: true,
        message: 'Votre paiement a été traité avec succès. Veuillez vérifier votre abonnement.',
        subscription: { plan_id: planId, status: 'active' },
        paymentProcessed: true
      };
    }
    
    // Si on arrive ici, c'est une erreur inattendue
  }
};

/**
 * Met à jour la méthode de paiement
{{ ... }}
 * @param {string} paymentMethodId - ID de la méthode de paiement
 * @returns {Promise<Object>} - La réponse du serveur
 */
export const updatePaymentMethod = async (userId, paymentMethodId) => {
  try {
    const response = await api.put(`/subscriptions/${userId}/payment-method`, {
      paymentMethodId
    });
    return response.data.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la méthode de paiement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour de la méthode de paiement');
  }
};

/**
 * Annule l'abonnement actif d'un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<Object>} - Réponse du serveur
 */
const cancelSubscription = async (userId) => {
  try {
    const response = await api.post(`/api/contracts/user/${userId}/cancel-subscription`);
    
    if (response.data && response.data.success) {
      return {
        success: true,
        message: 'Abonnement annulé avec succès',
        data: response.data.data
      };
    }
    
    throw new Error(response.data?.message || 'Erreur lors de l\'annulation de l\'abonnement');
  } catch (error) {
    console.error('Erreur lors de l\'annulation de l\'abonnement:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de l\'annulation de l\'abonnement');
  }
};

export default {
  getSubscription,
  subscribeToPlan,
  cancelSubscription,
  updatePaymentMethod,
};
